import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useMasterData,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import DialogCloseButton from './DialogCloseButton';
import IconLibrary from '../IconLibrary';

function DispatchJobDialog({
  open,
  onClose,
  jobId,
  jobNumber,
  recordVersion,
  reloadJob,
  setError,
}) {
  const { formatMessage } = useLocalization();
  const masterData = useMasterData();
  const dispatchCalendarEnabled =
    masterData?.applicationFeatureDemo?.dispatchCalendar || false;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, post, getApiUrl } = useDataProvider(provider);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const buttons = [
    {
      label: formatMessage({
        id: 'CLOSE',
        defaultMessage: 'Close',
      }),
      onClick: () => reloadJob(),
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Close />,
    },
  ];

  const setDispatchErrors = apiResponse => {
    if (Array.isArray(apiResponse?.errors) && apiResponse?.errors.length > 0) {
      apiResponse?.errors?.forEach((error, index) => {
        setError(error?.path || `root.serverError${index}`, {
          message: error?.message || error?.detail || JSON.stringify(error),
        });
      });
    } else {
      setError(`root.serverError`, {
        message: 'Dispatch Advisor: Could not determine error',
      });
    }
  };

  const dispatchJob = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/dispatch`;
    const response = await post(url, {
      recordVersion,
    });

    return response;
  };

  const getJobDispatchOptions = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/dispatch-options`,
    );
    return response;
  };

  const doGeneralDispatchJob = async () => {
    const dispatchJobResponse = await dispatchJob();
    if (dispatchJobResponse?.ok) {
      setSuccess(true);
    } else {
      setDispatchErrors(dispatchJobResponse);
      setLoading(false);
      onClose();
    }
  };

  // Dispatch action based on priority
  const dispatchNextAction = async data => {
    const firstDispatchMethod = data.routingPriority.find(method =>
      data.dispatchOptions.some(
        option => option.dispatchMethod === method && option.canDispatch,
      ),
    );

    switch (firstDispatchMethod) {
      case 'PRESET_CALENDAR_BASED_SCHEDULING': {
        navigate(`/servicejobs/dispatch/${jobId}`);
        break;
      }
      case 'MANUAL': {
        navigate(`/servicejobs/map/${jobId}`);
        break;
      }
      default: {
        await doGeneralDispatchJob();
        break;
      }
    }
  };

  const initialize = async () => {
    setLoading(true);
    if (dispatchCalendarEnabled) {
      const dispatchOptionsResponse = await getJobDispatchOptions();

      if (dispatchOptionsResponse?.ok) {
        const result = dispatchOptionsResponse?.data;
        dispatchNextAction(result);
      } else {
        setDispatchErrors(dispatchOptionsResponse);
        onClose();
      }
    } else {
      await doGeneralDispatchJob();
    }
    setLoading(false);
  };

  const onDialogClose = () => {
    if (success) {
      reloadJob(null);
    } else {
      onClose(null);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onDialogClose(null);
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Dispatch />
        <FormattedMessage id="DISPATCH_JOB" defaultMessage="Dispatch Job" />
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onDialogClose(null);
          }}
        />
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box textAlign="center">
            <CircularProgress size="30px" />
            <Typography>
              <FormattedMessage
                id="JOB_DISPATCH_IN_PROGRESS"
                defaultMessage="Job dispatch in progress"
              />
              ...
            </Typography>
          </Box>
        )}
        {success && (
          <Box sx={{ mt: 2 }} textAlign="center">
            <Typography>
              <FormattedMessage
                id="JOB_DISPATCHED_SUCCESSFULLY"
                defaultMessage="Job Dispatched Successfully"
              />
            </Typography>
          </Box>
        )}
      </DialogContent>
      {success && (
        <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
          {buttons.map(btn => {
            const { label, ...rest } = btn;
            return (
              <Button key={label} {...rest}>
                {label}
              </Button>
            );
          })}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default DispatchJobDialog;
